// Copied verbatim from tab-portal src/lib/name-identification (qa c50f5268), the portal mirror of
// core-api src/utils/nameIdentification. Rules only: no AI pass. Keep the three in step.
import { TinHint, TinHintSource } from './types'

export type ResolvedTinHint = {
  hint: TinHint
  source?: TinHintSource
}

/**
 * Derive a person/business hint from TIN *formatting*.
 *
 * IRS punctuation is unambiguous and is the strongest signal available:
 *   SSN  123-45-6789  -> individual
 *   EIN  12-3456789   -> business
 *
 * A bare 9-digit string carries no format information, so it stays "unknown"
 * and the name heuristics decide. We deliberately do NOT guess from prefix
 * ranges — EIN prefixes overlap reissued SSN ranges and a wrong guess here
 * would override a correct name signal.
 *
 * The TIN itself never leaves this module: only the derived hint is returned,
 * and only the hint is ever attached to a classification request.
 */
export const tinHintFromValue = (raw: string | null | undefined): TinHint => {
  const value = String(raw ?? '').trim()
  if (!value) return 'unknown'

  if (/^\d{3}-\d{2}-\d{4}$/.test(value)) return 'individual'
  if (/^\d{2}-\d{7}$/.test(value)) return 'business'

  return 'unknown'
}

/** Map declared RECIPIENT_ID_TYPE / TAX_ID_TYPE: 1=EIN, 2=SSN/ITIN/ATIN. */
export const tinHintFromIdType = (
  raw: string | number | null | undefined,
): TinHint => {
  const text = String(raw ?? '').trim()
  const normalized = /^\d+(?:\.0+)?$/.test(text)
    ? String(parseInt(text, 10))
    : text
  if (normalized === '1') return 'business'
  if (normalized === '2') return 'individual'
  return 'unknown'
}

/** Dashed TIN format wins over declared ID type when both are present. */
export const resolveTinHint = (
  tinRaw: string | null | undefined,
  idTypeRaw: string | number | null | undefined,
): ResolvedTinHint => {
  const fromTin = tinHintFromValue(tinRaw)
  if (fromTin !== 'unknown') {
    return { hint: fromTin, source: 'tin-format' }
  }
  const fromType = tinHintFromIdType(idTypeRaw)
  if (fromType !== 'unknown') {
    return { hint: fromType, source: 'id-type' }
  }
  return { hint: 'unknown' }
}

/**
 * Recipient TIN column per form — mirrors LEGACY_NAME_COLUMNS_BY_FORM.
 * 1099-K keys the payee rather than a recipient.
 */
export const TIN_COLUMN_BY_FORM: Record<string, string> = {
  '1099-NEC': 'RECIPIENT_ID_NUMBER',
  '1099-MISC': 'RECIPIENT_ID_NUMBER',
  '1099-R': 'RECIPIENT_ID_NUMBER',
  '1099-DIV': 'RECIPIENT_ID_NUMBER',
  '1099-C': 'RECIPIENT_ID_NUMBER',
  '1099-INT': 'RECIPIENT_ID_NUMBER',
  '1099-B': 'RECIPIENT_ID_NUMBER',
  '1099-Q': 'RECIPIENT_ID_NUMBER',
  '1099-K': 'PAYEE_ID_NUMBER',
  '1098-T': 'STUDENT_SSN',
  '1098': 'BORROWER_ID_NUMBER',
}

/** Same normalization as legacyNameColumnsForForm — "1099-NEC" / "1099NEC". */
const normalizeFormKey = (formType: string): string =>
  String(formType ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

const TIN_COLUMN_NORMALIZED: Record<string, string> = Object.fromEntries(
  Object.entries(TIN_COLUMN_BY_FORM).map(([key, value]) => [
    normalizeFormKey(key),
    value,
  ]),
)

export const tinColumnForForm = (formType: string): string | null =>
  TIN_COLUMN_NORMALIZED[normalizeFormKey(formType)] ?? null

const ID_TYPE_COLUMN_BY_FORM: Record<string, string> = {
  '1099-K': 'TAX_ID_TYPE',
}

const ID_TYPE_COLUMN_NORMALIZED: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(TIN_COLUMN_BY_FORM)
      .filter(([key]) => key !== '1099-K')
      .map(([key]) => [normalizeFormKey(key), 'RECIPIENT_ID_TYPE']),
  ),
  ...Object.fromEntries(
    Object.entries(ID_TYPE_COLUMN_BY_FORM).map(([key, value]) => [
      normalizeFormKey(key),
      value,
    ]),
  ),
}

export const idTypeColumnForForm = (formType: string): string | null =>
  ID_TYPE_COLUMN_NORMALIZED[normalizeFormKey(formType)] ?? null
